import { ReactNode } from 'react'
import {
  Menu,
  MenuItem,
  MenuProps,
  ListItemIcon,
  ListItemText,
  Link,
  Divider,
} from '@mui/material'
import useOverlay, { OverlayState } from '@/hooks/useOverlay'
import {
  Delete,
  Link as LinkIcon,
  ContentCopy,
  OpenInNew,
  Download,
  Rule,
  Description,
  ExitToApp,
} from '@mui/icons-material'
import { Document } from '@/types/documents'
import { WorkflowStateEvent } from '@/types/workflow-state-events'
import { useRunAllRulesWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import useRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { useTriggerWorkflowStateEvent } from '@/service-library/hooks/events'
import { useGetWorkflowStateEvents } from '@/service-library/hooks/workflow-state-events'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { handleDownloadOriginalDocument } from '@/utils/download-file'
import {
  showErrorSnackbar,
  showInfoSnackbar,
  showSuccessSnackbar,
} from '@/utils/snackbars'
import { useDemoModeContext } from '@/components/demo-mode-provider/DemoModeProvider'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useDocumentsChangeSetsContext } from '@/components/validation/providers/DocumentsChangeSetsProvider'
import WorkflowStateDescriptionDialog from '@/components/workflow-state-page/WorkflowStateDescriptionDialog'
import DeleteDocumentsDialog from '@/components/workflows/DeleteDocumentsDialog'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import copyToClipboard from './copy-to-clipboard'

type DocumentActionsMenuProps = {
  /** Overlay state. */
  overlay: OverlayState

  /** ID the project belongs to. */
  projectId: string

  basicOptions?: boolean

  /** Target document ID. */
  documentId?: string

  superUserItems?: ReactNode[]

  /** Callback that runs before the document is deleted. */
  onBeforeDelete?: () => void

  /** Callback that runs after the document is successfully deleted. */
  onDelete?: () => void

  /** Any additional menu items can be passed in as children. */
  children?: ReactNode
} & Partial<MenuProps>

export default function DocumentActionsMenu({
  overlay,
  children,
  projectId,
  basicOptions = false,
  documentId,
  superUserItems,
  onBeforeDelete,
  onDelete,
  ...rest
}: DocumentActionsMenuProps) {
  const { document } = useDocumentContext()
  const { getCurrentPathWithParams } = useLocationHelpers()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const getRelevantWorkflowState = useRelevantWorkflowState()
  const relevantWorkflowState = getRelevantWorkflowState(document as Document)
  const documentsChangeSetsContextData = useDocumentsChangeSetsContext()
  const isSuperUser = useIsSuperUser()
  const [demoMode] = useDemoModeContext()

  const documentationOverlay = useOverlay()
  const deleteDocsOverlay = useOverlay()

  const { workflowStateEvents } = useGetWorkflowStateEvents({
    filters: {
      workflow_state_id: relevantWorkflowState?.id || '',
      code__in: 'on_enter,on_exit',
    },
  })

  const { runRules } = useRunAllRulesWithDocChange({
    documentIds: documentId ? [documentId] : [],
    workflowId: selectedWorkflow?.id || '',
    workflowStateId: relevantWorkflowState?.id || '',
    documentsChangeSetsContextData,
    onError: () => {
      showErrorSnackbar('Document validation failed. Please try again later.')
    },
  })

  const { triggerWorkflowStateEvent } = useTriggerWorkflowStateEvent()

  const currentPath = getCurrentPathWithParams()

  const pathToDocument = `${currentPath}${
    currentPath.includes('document_id')
      ? ''
      : currentPath.includes('?')
      ? `&document_id=${documentId}`
      : `?document_id=${documentId}`
  }`

  function handleDeleteDocument() {
    if (!documentId) return

    deleteDocsOverlay.open()
    overlay.close()
  }

  function handleReRunRules(forceClearCache = false) {
    if (!documentId) return
    showInfoSnackbar('Validating Document')
    runRules(forceClearCache)
    overlay.close()
  }

  function triggerEvent(eventCode: string) {
    if (!documentId) return
    const event = workflowStateEvents.find(
      (event) => event.code === eventCode,
    ) as WorkflowStateEvent
    triggerWorkflowStateEvent([
      {
        workflow_state_event_id: event.id,
        document_id: documentId,
      },
    ])
    overlay.close()
  }

  return (
    <>
      <Menu
        open={overlay.isOpen}
        onClose={overlay.close}
        anchorEl={overlay.anchorEl as Element}
        disableAutoFocusItem
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        {...rest}
      >
        {children}

        {relevantWorkflowState && (
          <MenuItem
            onClick={() => {
              if (!documentId) return
              documentationOverlay.open()
              overlay.close()
            }}
          >
            <ListItemIcon>
              <Description />
            </ListItemIcon>
            <ListItemText>Workflow State Documentation</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            if (!documentId) return
            copyToClipboard(`${window.location.host}${pathToDocument}`)
            showInfoSnackbar('Document link copied to clipboard')
            overlay.close()
          }}
        >
          <ListItemIcon>
            <LinkIcon />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>

        <MenuItem
          component={Link}
          target="_blank"
          href={pathToDocument}
          onClick={overlay.close}
        >
          <ListItemIcon>
            <OpenInNew />
          </ListItemIcon>
          <ListItemText>Open in New Tab</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (!documentId) return
            handleDownloadOriginalDocument(documentId)
            overlay.close()
          }}
        >
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (!documentId) return
            copyToClipboard(documentId)
            showInfoSnackbar('Document ID copied to clipboard')
            overlay.close()
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>Copy ID</ListItemText>
        </MenuItem>

        {!basicOptions && relevantWorkflowState && (
          <MenuItem
            onClick={() => {
              handleReRunRules()
            }}
          >
            <ListItemIcon>
              <Rule />
            </ListItemIcon>
            <ListItemText>Re-Run Rules</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleDeleteDocument()
          }}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>

        {isSuperUser && !demoMode && (
          <>
            {relevantWorkflowState && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleReRunRules(true)
                  }}
                  sx={{ color: SUPER_USER_ONLY_COLOR }}
                >
                  <ListItemIcon>
                    <Rule
                      sx={{
                        color: SUPER_USER_ONLY_COLOR,
                      }}
                    />
                  </ListItemIcon>
                  Re-Run Rules (Clear Cache)
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    triggerEvent('on_enter')
                  }}
                  sx={{ color: SUPER_USER_ONLY_COLOR }}
                >
                  <ListItemIcon>
                    <ExitToApp
                      sx={{
                        color: SUPER_USER_ONLY_COLOR,
                      }}
                    />
                  </ListItemIcon>
                  Run On Enter Event
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    triggerEvent('on_exit')
                  }}
                  sx={{ color: SUPER_USER_ONLY_COLOR }}
                >
                  <ListItemIcon>
                    <ExitToApp
                      sx={{
                        color: SUPER_USER_ONLY_COLOR,
                        transform: 'rotate(180deg)',
                      }}
                    />
                  </ListItemIcon>
                  Run On Exit Event
                </MenuItem>
              </>
            )}
            {superUserItems && !relevantWorkflowState && <Divider />}
            {superUserItems}
          </>
        )}
      </Menu>

      {relevantWorkflowState && (
        <WorkflowStateDescriptionDialog
          overlay={documentationOverlay}
          projectId={projectId}
          workflowState={relevantWorkflowState}
        />
      )}

      {documentId && (
        <DeleteDocumentsDialog
          overlay={deleteDocsOverlay}
          documentIds={[documentId]}
          onDeleteError={() => {
            showErrorSnackbar(
              'Failed to delete document. Please try again later.',
            )
          }}
          onDeleteSuccess={() => {
            showSuccessSnackbar('Document Deleted')
            onDelete?.()
            onBeforeDelete?.()
          }}
        />
      )}
    </>
  )
}
